/**
 * LPA Schedules API
 *
 * CRUD operations for `core.mod_lpa_schedules` and question sync
 * for `core.mod_lpa_schedule_questions`.
 *
 * Schedules are audit-scoped — each schedule belongs to exactly one audit
 * and represents a "layer" (e.g., "Operator daily", "Supervisor weekly").
 *
 * Question sync uses a full-replace strategy with guard rails:
 * questions with existing responses in completions cannot be removed,
 * only deactivated (is_active=false).
 */

import { supabase } from '@/integrations/supabase/client';
import type {
  LpaSchedule,
  LpaScheduleWithDetails,
  LpaScheduleQuestionWithDetails,
  CreateScheduleInput,
  UpdateScheduleInput,
  SyncScheduleQuestionsInput,
} from '../types';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Resolves auditor profiles for a set of schedules.
 * Returns a map of auditor_id → profile data.
 */
async function resolveAuditorProfiles(
  auditorIds: string[],
): Promise<
  Record<
    string,
    { id: string; full_name: string | null; email: string; avatar_url: string | null }
  >
> {
  const uniqueIds = [...new Set(auditorIds.filter(Boolean))];
  if (uniqueIds.length === 0) return {};

  const { data, error } = await supabase
    .schema('core')
    .from('profiles')
    .select('id, full_name, email, avatar_url')
    .in('id', uniqueIds);

  if (error) throw error;

  const map: Record<
    string,
    { id: string; full_name: string | null; email: string; avatar_url: string | null }
  > = {};
  for (const p of data ?? []) {
    map[p.id] = p as {
      id: string;
      full_name: string | null;
      email: string;
      avatar_url: string | null;
    };
  }
  return map;
}

/**
 * Resolves question counts (total + active) for a set of schedule IDs.
 * Returns a map of schedule_id → { total, active }.
 */
async function resolveQuestionCounts(
  scheduleIds: string[],
): Promise<Record<string, { total: number; active: number }>> {
  if (scheduleIds.length === 0) return {};

  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_schedule_questions')
    .select('schedule_id, is_active')
    .in('schedule_id', scheduleIds);

  if (error) throw error;

  const counts: Record<string, { total: number; active: number }> = {};
  for (const row of data ?? []) {
    if (!counts[row.schedule_id]) {
      counts[row.schedule_id] = { total: 0, active: 0 };
    }
    counts[row.schedule_id].total += 1;
    if (row.is_active) {
      counts[row.schedule_id].active += 1;
    }
  }
  return counts;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Fetch all schedules for an audit, with auditor profiles and question counts.
 * Returns newest first.
 */
export async function fetchSchedulesByAuditId(
  auditId: string,
): Promise<LpaScheduleWithDetails[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_schedules')
    .select('*')
    .eq('audit_id', auditId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  const schedules = (data ?? []) as unknown as LpaSchedule[];

  // Resolve related data in parallel
  const [auditorMap, questionCounts] = await Promise.all([
    resolveAuditorProfiles(schedules.map((s) => s.auditor_id).filter(Boolean) as string[]),
    resolveQuestionCounts(schedules.map((s) => s.id)),
  ]);

  return schedules.map((s) => ({
    ...s,
    auditor: s.auditor_id ? (auditorMap[s.auditor_id] ?? null) : null,
    question_count: questionCounts[s.id]?.total ?? 0,
    active_question_count: questionCounts[s.id]?.active ?? 0,
  }));
}

/**
 * Fetch a single schedule by ID, with auditor profile and question counts.
 */
export async function fetchScheduleById(
  scheduleId: string,
): Promise<LpaScheduleWithDetails | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_schedules')
    .select('*')
    .eq('id', scheduleId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const schedule = data as unknown as LpaSchedule;

  const [auditorMap, questionCounts] = await Promise.all([
    resolveAuditorProfiles(schedule.auditor_id ? [schedule.auditor_id] : []),
    resolveQuestionCounts([schedule.id]),
  ]);

  return {
    ...schedule,
    auditor: schedule.auditor_id ? (auditorMap[schedule.auditor_id] ?? null) : null,
    question_count: questionCounts[schedule.id]?.total ?? 0,
    active_question_count: questionCounts[schedule.id]?.active ?? 0,
  };
}

/**
 * Fetch schedule questions with full question details for a schedule.
 * Returns questions ordered by sort_order.
 * Also checks whether each question has responses (to enforce removal guard).
 */
export async function fetchScheduleQuestions(
  scheduleId: string,
): Promise<LpaScheduleQuestionWithDetails[]> {
  // 1. Get schedule question assignments
  const { data: sqData, error: sqError } = await supabase
    .schema('core')
    .from('mod_lpa_schedule_questions')
    .select('*')
    .eq('schedule_id', scheduleId)
    .order('sort_order', { ascending: true });

  if (sqError) throw sqError;

  const assignments = (sqData ?? []) as unknown as Array<{
    id: string;
    tenant_id: string;
    schedule_id: string;
    question_id: string;
    is_active: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
  }>;

  if (assignments.length === 0) return [];

  const questionIds = [...new Set(assignments.map((a) => a.question_id))];

  // 2. Resolve question details
  const { data: qData, error: qError } = await supabase
    .schema('core')
    .from('mod_lpa_questions')
    .select('id, question, question_translations, explanation, is_active')
    .in('id', questionIds);

  if (qError) throw qError;

  const questionMap: Record<
    string,
    {
      id: string;
      question: string;
      question_translations: Record<string, string>;
      explanation: string | null;
      is_active: boolean;
    }
  > = {};
  for (const q of qData ?? []) {
    questionMap[q.id] = q as {
      id: string;
      question: string;
      question_translations: Record<string, string>;
      explanation: string | null;
      is_active: boolean;
    };
  }

  // 3. Check which schedule_questions have responses (via completions)
  // Responses reference schedule_question_id, not question_id directly.
  const scheduleQuestionIds = assignments.map((a) => a.id);
  const { data: responseData, error: responseError } = await supabase
    .schema('core')
    .from('mod_lpa_responses')
    .select('schedule_question_id')
    .in('schedule_question_id', scheduleQuestionIds);

  if (responseError) throw responseError;

  const sqIdsWithResponses = new Set(
    (responseData ?? []).map((r: { schedule_question_id: string }) => r.schedule_question_id),
  );

  // 4. Assemble result
  return assignments.map((a) => ({
    ...a,
    question: questionMap[a.question_id] ?? {
      id: a.question_id,
      question: '[Deleted question]',
      question_translations: {},
      explanation: null,
      is_active: false,
    },
    has_responses: sqIdsWithResponses.has(a.id),
  }));
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Create a new schedule. */
export async function createSchedule(
  input: CreateScheduleInput,
): Promise<LpaSchedule> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_schedules')
    .insert({
      tenant_id: input.tenant_id,
      audit_id: input.audit_id,
      name: input.name,
      frequency: input.frequency,
      auditor_id: input.auditor_id ?? null,
      is_active: input.is_active ?? true,
      start_date: input.start_date,
      end_date: input.end_date ?? null,
    })
    .select()
    .single();

  if (error) throw error;
  return data as unknown as LpaSchedule;
}

/** Update an existing schedule. Only provided fields are changed. */
export async function updateSchedule(
  scheduleId: string,
  input: UpdateScheduleInput,
): Promise<LpaSchedule> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_schedules')
    .update(input)
    .eq('id', scheduleId)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as LpaSchedule;
}

/**
 * Delete a schedule.
 *
 * Only allowed if no instances have been generated for this schedule.
 * The caller (UI/hook) checks preconditions; DB foreign key constraints
 * will also prevent deletion if instances reference this schedule.
 */
export async function deleteSchedule(scheduleId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('mod_lpa_schedules')
    .delete()
    .eq('id', scheduleId);

  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Question Sync
// ---------------------------------------------------------------------------

/**
 * Sync questions for a schedule using full-replace strategy.
 *
 * Receives the desired state: an array of { question_id, sort_order, is_active }.
 *
 * Guard rails:
 * - Questions that have responses in completions CANNOT be removed from the
 *   schedule. If the caller tries to remove such a question, it is kept but
 *   set to is_active=false automatically. The returned list includes these
 *   force-kept questions.
 *
 * @param scheduleId - The schedule to sync questions for.
 * @param tenantId - The tenant ID (required for new inserts).
 * @param assignments - Desired question list with sort_order and is_active.
 * @returns The full resolved list of schedule questions after sync.
 */
export async function syncScheduleQuestions(
  scheduleId: string,
  tenantId: string,
  assignments: SyncScheduleQuestionsInput[],
): Promise<LpaScheduleQuestionWithDetails[]> {
  // 1. Fetch current assignments for this schedule
  const { data: currentData, error: currentError } = await supabase
    .schema('core')
    .from('mod_lpa_schedule_questions')
    .select('id, question_id')
    .eq('schedule_id', scheduleId);

  if (currentError) throw currentError;

  const currentAssignments = (currentData ?? []) as Array<{
    id: string;
    question_id: string;
  }>;

  // Map current question_id → schedule_question row id
  const currentMap = new Map(
    currentAssignments.map((c) => [c.question_id, c.id]),
  );

  // Desired question IDs from input
  const desiredQuestionIds = new Set(assignments.map((a) => a.question_id));

  // 2. Determine which current assignments to remove (not in desired list)
  const toRemoveIds: string[] = [];
  for (const current of currentAssignments) {
    if (!desiredQuestionIds.has(current.question_id)) {
      toRemoveIds.push(current.id);
    }
  }

  // 3. Check if any to-be-removed have responses (guard rail)
  let forceKeptQuestionIds = new Set<string>();
  if (toRemoveIds.length > 0) {
    const { data: responseData, error: responseError } = await supabase
      .schema('core')
      .from('mod_lpa_responses')
      .select('schedule_question_id')
      .in('schedule_question_id', toRemoveIds);

    if (responseError) throw responseError;

    const protectedSqIds = new Set(
      (responseData ?? []).map((r: { schedule_question_id: string }) => r.schedule_question_id),
    );

    // Split: safe to remove vs. force-keep
    const safeToRemoveIds = toRemoveIds.filter((id) => !protectedSqIds.has(id));
    const forceKeepIds = toRemoveIds.filter((id) => protectedSqIds.has(id));

    // Delete safe-to-remove
    if (safeToRemoveIds.length > 0) {
      const { error: deleteError } = await supabase
        .schema('core')
        .from('mod_lpa_schedule_questions')
        .delete()
        .in('id', safeToRemoveIds);

      if (deleteError) throw deleteError;
    }

    // Force-keep: deactivate instead of removing
    if (forceKeepIds.length > 0) {
      const { error: deactivateError } = await supabase
        .schema('core')
        .from('mod_lpa_schedule_questions')
        .update({ is_active: false })
        .in('id', forceKeepIds);

      if (deactivateError) throw deactivateError;

      // Track which question_ids were force-kept
      forceKeptQuestionIds = new Set(
        currentAssignments
          .filter((c) => forceKeepIds.includes(c.id))
          .map((c) => c.question_id),
      );
    }
  }

  // 4. Upsert desired assignments (insert new, update existing)
  for (const assignment of assignments) {
    const existingId = currentMap.get(assignment.question_id);

    if (existingId) {
      // Update existing
      const { error: updateError } = await supabase
        .schema('core')
        .from('mod_lpa_schedule_questions')
        .update({
          sort_order: assignment.sort_order,
          is_active: assignment.is_active,
        })
        .eq('id', existingId);

      if (updateError) throw updateError;
    } else {
      // Insert new
      const { error: insertError } = await supabase
        .schema('core')
        .from('mod_lpa_schedule_questions')
        .insert({
          tenant_id: tenantId,
          schedule_id: scheduleId,
          question_id: assignment.question_id,
          sort_order: assignment.sort_order,
          is_active: assignment.is_active,
        });

      if (insertError) throw insertError;
    }
  }

  // 5. Return the full resolved list
  return fetchScheduleQuestions(scheduleId);
}
